import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea, ScrollBar } from '../../components/ui/scroll-area';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, Loader2, Sun, CloudSun, Building, Users, Timer, AlertCircle, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BoardMesaiDurumu = () => {
  const { user } = useAuth();
  const { branchId } = useParams();
  
  // Branch info
  const [branchName, setBranchName] = useState('');
  
  // Company/Subcontractor selection
  const [selectedCompany, setSelectedCompany] = useState('fortis');
  const [subcontractors, setSubcontractors] = useState([]);
  
  // Inner tab selection
  const [activeTab, setActiveTab] = useState('daily'); // 'daily', 'monthly', 'overtime'
  
  // Daily attendance state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [dailyAttendance, setDailyAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Monthly attendance state
  const [monthlyDate, setMonthlyDate] = useState(new Date());
  const [monthlyData, setMonthlyData] = useState([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  
  // Overtime state
  const [overtimeYear, setOvertimeYear] = useState(new Date().getFullYear());
  const [overtimeMonth, setOvertimeMonth] = useState(new Date().getMonth() + 1);
  const [overtimeData, setOvertimeData] = useState([]);
  const [overtimeLoading, setOvertimeLoading] = useState(false);
  
  // Sorting state for daily attendance
  const [dailySortConfig, setDailySortConfig] = useState({ key: null, direction: 'asc' });

  const formatDateStr = (date) => date.toISOString().split('T')[0];
  
  const formatDisplayDate = (date) => {
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' });
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('fortis_token');
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch branch info and subcontractors on mount
  useEffect(() => {
    fetchBranchInfo();
    fetchSubcontractors();
  }, [branchId]);

  // Fetch employees and attendance when company or date changes
  useEffect(() => {
    if (branchId) {
      fetchEmployees();
      fetchDailyAttendance();
    }
  }, [branchId, selectedCompany, currentDate]);

  // Fetch monthly data when month changes
  useEffect(() => {
    if (activeTab === 'monthly' && branchId) {
      fetchMonthlyAttendance();
    }
  }, [branchId, selectedCompany, monthlyDate, activeTab]);

  // Fetch overtime data when year/month changes
  useEffect(() => {
    if (activeTab === 'overtime' && branchId) {
      fetchOvertimeData();
    }
  }, [branchId, selectedCompany, overtimeYear, overtimeMonth, activeTab]);

  const fetchBranchInfo = async () => {
    try {
      const response = await axios.get(`${API}/board/branches`, { headers: getAuthHeaders() });
      const branch = response.data?.find(b => b.id === branchId);
      if (branch) {
        setBranchName(branch.name);
      }
    } catch (error) {
      console.error('Error fetching branch info:', error);
    }
  };

  const fetchSubcontractors = async () => {
    try {
      const response = await axios.get(`${API}/board/branch/${branchId}/subcontractors`, { headers: getAuthHeaders() });
      setSubcontractors(response.data || []);
    } catch (error) {
      console.error('Error fetching subcontractors:', error);
      setSubcontractors([]);
    }
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/board/branch/${branchId}/employees`, { headers: getAuthHeaders() });
      
      // Filter by company/subcontractor
      let filtered = response.data || [];
      
      if (selectedCompany === 'fortis') {
        filtered = filtered.filter(emp => !emp.subcontractor_id);
      } else {
        filtered = filtered.filter(emp => emp.subcontractor_id === selectedCompany);
      }
      
      setEmployees(filtered);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Çalışanlar yüklenirken hata oluştu');
      setEmployees([]);
    }
    setLoading(false);
  };

  const fetchDailyAttendance = async () => {
    try {
      const dateStr = formatDateStr(currentDate);
      const response = await axios.get(`${API}/board/branch/${branchId}/daily-attendance?date=${dateStr}`, { headers: getAuthHeaders() });
      setDailyAttendance(response.data?.records || []);
    } catch (error) {
      console.error('Error fetching daily attendance:', error);
      setDailyAttendance([]);
    }
  };

  const fetchMonthlyAttendance = async () => {
    setMonthlyLoading(true);
    try {
      const year = monthlyDate.getFullYear();
      const month = monthlyDate.getMonth() + 1;
      
      const response = await axios.get(`${API}/board/branch/${branchId}/monthly-attendance/${year}/${month}`, { headers: getAuthHeaders() });
      
      // Filter by company/subcontractor
      let filtered = response.data || [];
      
      if (selectedCompany === 'fortis') {
        filtered = filtered.filter(emp => !emp.subcontractor_id);
      } else {
        filtered = filtered.filter(emp => emp.subcontractor_id === selectedCompany);
      }
      
      setMonthlyData(filtered);
    } catch (error) {
      console.error('Error fetching monthly attendance:', error);
      toast.error('Aylık mesai verileri yüklenirken hata oluştu');
      setMonthlyData([]);
    }
    setMonthlyLoading(false);
  };

  const fetchOvertimeData = async () => {
    setOvertimeLoading(true);
    try {
      // Get overtime data - employees already include overtime_records
      setOvertimeData(employees);
    } catch (error) {
      console.error('Error fetching overtime data:', error);
    }
    setOvertimeLoading(false);
  };

  const getDailyAttendanceForEmployee = (employeeId, employeeName) => {
    return dailyAttendance.find(a => 
      a.employee_id === employeeId || 
      (a.employee_name && a.employee_name.toLowerCase() === employeeName?.toLowerCase())
    );
  };

  const navigateDate = (days) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + days);
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  const navigateMonth = (direction) => {
    const newDate = new Date(monthlyDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setMonthlyDate(newDate);
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getStatusBadge = (status) => {
    const config = {
      'tam_gun': { label: 'G', color: 'bg-green-500 text-white', title: 'Tam Gün' },
      'yarim_gun': { label: 'Y', color: 'bg-yellow-500 text-white', title: 'Yarım Gün' },
      'izinli': { label: 'İ', color: 'bg-blue-500 text-white', title: 'Ücretli İzinli' },
      'ucretsiz_izinli': { label: 'Üİ', color: 'bg-orange-500 text-white', title: 'Ücretsiz İzinli' },
      'raporlu': { label: 'R', color: 'bg-purple-500 text-white', title: 'Raporlu' },
      'yillik_izin': { label: 'Yİ', color: 'bg-cyan-500 text-white', title: 'Yıllık İzin' },
      'yok': { label: 'X', color: 'bg-red-500 text-white', title: 'Gelmedi' },
      'tatil': { label: 'T', color: 'bg-gray-400 text-white', title: 'Resmi Tatil' },
      'present': { label: 'G', color: 'bg-green-500 text-white', title: 'Tam Gün' },
      'half_day': { label: 'Y', color: 'bg-yellow-500 text-white', title: 'Yarım Gün' },
      'paid_leave': { label: 'İ', color: 'bg-blue-500 text-white', title: 'Ücretli İzinli' },
      'unpaid_leave': { label: 'Üİ', color: 'bg-orange-500 text-white', title: 'Ücretsiz İzinli' },
      'sick': { label: 'R', color: 'bg-purple-500 text-white', title: 'Raporlu' },
      'annual': { label: 'Yİ', color: 'bg-cyan-500 text-white', title: 'Yıllık İzin' },
      'absent': { label: 'X', color: 'bg-red-500 text-white', title: 'Gelmedi' },
      'holiday': { label: 'T', color: 'bg-gray-400 text-white', title: 'Resmi Tatil' }
    };
    const c = config[status] || { label: '-', color: 'bg-gray-200 text-gray-600', title: 'Kayıt Yok' };
    return (
      <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${c.color}`} title={c.title}>
        {c.label}
      </span>
    );
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      return new Date(isoString).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '-';
    }
  };

  const handleDailySort = (key) => {
    let direction = 'asc';
    if (dailySortConfig.key === key && dailySortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setDailySortConfig({ key, direction });
  };

  const getSortedEmployees = () => {
    if (!dailySortConfig.key) return employees;
    
    return [...employees].sort((a, b) => {
      const attA = getDailyAttendanceForEmployee(a.id, a.name);
      const attB = getDailyAttendanceForEmployee(b.id, b.name);
      
      let valueA, valueB;
      
      if (dailySortConfig.key === 'morning') {
        const timeA = attA?.morning_time || attA?.sabah_time || '';
        const timeB = attB?.morning_time || attB?.sabah_time || '';
        valueA = timeA ? (typeof timeA === 'string' && timeA.includes(':') ? timeA : formatTime(timeA)) : 'zzz';
        valueB = timeB ? (typeof timeB === 'string' && timeB.includes(':') ? timeB : formatTime(timeB)) : 'zzz';
      } else if (dailySortConfig.key === 'afternoon') {
        const timeA = attA?.afternoon_time || attA?.oglen_time || '';
        const timeB = attB?.afternoon_time || attB?.oglen_time || '';
        valueA = timeA ? (typeof timeA === 'string' && timeA.includes(':') ? timeA : formatTime(timeA)) : 'zzz';
        valueB = timeB ? (typeof timeB === 'string' && timeB.includes(':') ? timeB : formatTime(timeB)) : 'zzz';
      } else if (dailySortConfig.key === 'name') {
        valueA = a.name || '';
        valueB = b.name || '';
      } else if (dailySortConfig.key === 'position') {
        valueA = a.position_name || a.gorev || '';
        valueB = b.position_name || b.gorev || '';
      } else if (dailySortConfig.key === 'profession') {
        valueA = a.profession || a.meslek || '';
        valueB = b.profession || b.meslek || '';
      }
      
      if (valueA < valueB) return dailySortConfig.direction === 'asc' ? -1 : 1;
      if (valueA > valueB) return dailySortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (key) => {
    if (dailySortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return dailySortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 text-amber-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-amber-600" />;
  };

  const companyTabs = [
    { id: 'fortis', name: 'FORTİS', icon: Building },
    ...subcontractors.map(s => ({ id: s.id, name: s.company_name || s.name, icon: Users }))
  ];

  const handleRefresh = () => {
    fetchEmployees();
    fetchDailyAttendance();
    if (activeTab === 'monthly') fetchMonthlyAttendance();
    toast.success('Veriler yenilendi');
  };

  if (loading && employees.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="board-mesai-durumu">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mesai Durumu</h1>
          <p className="text-gray-500">{branchName} şubesi çalışanlarının mesai durumları</p>
        </div>
        <Button 
          variant="outline"
          className="gap-2"
          onClick={handleRefresh}
          data-testid="refresh-btn"
        >
          <RefreshCw className="h-4 w-4" />
          Yenile
        </Button>
      </div>

      {/* Company/Subcontractor Tabs */}
      <Tabs value={selectedCompany} onValueChange={setSelectedCompany}>
        <TabsList className="flex flex-wrap gap-2 h-auto p-2 bg-[hsl(var(--muted))]">
          {companyTabs.map(company => {
            const Icon = company.icon;
            return (
              <TabsTrigger 
                key={company.id} 
                value={company.id}
                className="flex items-center gap-2 data-[state=active]:bg-[hsl(var(--brand-700))] data-[state=active]:text-white"
                data-testid={`company-tab-${company.id}`}
              >
                <Icon className="h-4 w-4" />
                {company.name}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {companyTabs.map(company => (
          <TabsContent key={company.id} value={company.id} className="mt-4">
            {/* Inner Tabs: GÜNLÜK MESAİ, AYLIK MESAİ, FAZLA MESAİLER */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="daily" className="flex items-center gap-2" data-testid="tab-daily">
                  <Clock className="h-4 w-4" />
                  GÜNLÜK MESAİ
                </TabsTrigger>
                <TabsTrigger value="monthly" className="flex items-center gap-2" data-testid="tab-monthly">
                  <CalendarIcon className="h-4 w-4" />
                  AYLIK MESAİ
                </TabsTrigger>
                <TabsTrigger value="overtime" className="flex items-center gap-2" data-testid="tab-overtime">
                  <Timer className="h-4 w-4" />
                  FAZLA MESAİLER
                </TabsTrigger>
              </TabsList>

              {/* GÜNLÜK MESAİ Tab */}
              <TabsContent value="daily">
                {/* Date Navigation */}
                <Card className="mb-4">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <Button variant="outline" size="sm" onClick={() => navigateDate(-1)}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="h-5 w-5 text-amber-600" />
                          <span className="font-medium text-lg">{formatDisplayDate(currentDate)}</span>
                        </div>
                        <Button variant="outline" size="sm" onClick={goToToday}>
                          Bugün
                        </Button>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => navigateDate(1)}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <Card className="bg-gray-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-gray-700">{employees.length}</p>
                      <p className="text-sm text-gray-600">Toplam Çalışan</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-green-700">
                        {dailyAttendance.filter(a => employees.some(e => e.id === a.employee_id) && a.morning_time).length}
                      </p>
                      <p className="text-sm text-green-600">Sabah Bildirimi</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-blue-700">
                        {dailyAttendance.filter(a => employees.some(e => e.id === a.employee_id) && a.afternoon_time).length}
                      </p>
                      <p className="text-sm text-blue-600">Öğle Bildirimi</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-amber-700">
                        {employees.length - dailyAttendance.filter(a => employees.some(e => e.id === a.employee_id) && (a.morning_time || a.afternoon_time)).length}
                      </p>
                      <p className="text-sm text-amber-600">Bildirim Yok</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Daily Attendance Table */}
                {employees.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <AlertCircle className="h-16 w-16 mx-auto mb-4 text-gray-200" />
                      <h3 className="text-xl font-bold mb-2">Çalışan Bulunmuyor</h3>
                      <p className="text-gray-500">
                        {selectedCompany === 'fortis' ? 'FORTİS' : subcontractors.find(s => s.id === selectedCompany)?.company_name || subcontractors.find(s => s.id === selectedCompany)?.name} 
                        kadrosunda bu şubede kayıtlı çalışan yok
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-amber-600" />
                        Günlük Mesai Tablosu - {selectedCompany === 'fortis' ? 'FORTİS' : subcontractors.find(s => s.id === selectedCompany)?.company_name || subcontractors.find(s => s.id === selectedCompany)?.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead 
                              className="font-bold cursor-pointer hover:bg-gray-100 select-none"
                              onClick={() => handleDailySort('name')}
                            >
                              <div className="flex items-center">
                                AD SOYAD
                                {getSortIcon('name')}
                              </div>
                            </TableHead>
                            <TableHead 
                              className="font-bold cursor-pointer hover:bg-gray-100 select-none"
                              onClick={() => handleDailySort('position')}
                            >
                              <div className="flex items-center">
                                GÖREV
                                {getSortIcon('position')}
                              </div>
                            </TableHead>
                            <TableHead 
                              className="font-bold cursor-pointer hover:bg-gray-100 select-none"
                              onClick={() => handleDailySort('profession')}
                            >
                              <div className="flex items-center">
                                MESLEK
                                {getSortIcon('profession')}
                              </div>
                            </TableHead>
                            <TableHead 
                              className="font-bold text-center cursor-pointer hover:bg-gray-100 select-none"
                              onClick={() => handleDailySort('morning')}
                            >
                              <div className="flex items-center justify-center gap-1">
                                <Sun className="h-4 w-4 text-amber-500" />
                                SABAH MESAİ
                                {getSortIcon('morning')}
                              </div>
                            </TableHead>
                            <TableHead 
                              className="font-bold text-center cursor-pointer hover:bg-gray-100 select-none"
                              onClick={() => handleDailySort('afternoon')}
                            >
                              <div className="flex items-center justify-center gap-1">
                                <CloudSun className="h-4 w-4 text-orange-500" />
                                ÖĞLE MESAİ
                                {getSortIcon('afternoon')}
                              </div>
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {getSortedEmployees().map(emp => {
                            const att = getDailyAttendanceForEmployee(emp.id, emp.name);
                            const morningTime = att?.morning_time || att?.sabah_time;
                            const afternoonTime = att?.afternoon_time || att?.oglen_time;
                            return (
                              <TableRow key={emp.id}>
                                <TableCell className="font-medium">{emp.name}</TableCell>
                                <TableCell>{emp.position_name || emp.gorev || '-'}</TableCell>
                                <TableCell>{emp.profession || emp.meslek || '-'}</TableCell>
                                <TableCell className="text-center">
                                  {morningTime ? (
                                    <Badge className="bg-green-100 text-green-700 font-mono">
                                      {typeof morningTime === 'string' && morningTime.includes(':') ? morningTime : formatTime(morningTime)}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-gray-400">
                                      Bildirim Yok
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  {afternoonTime ? (
                                    <Badge className="bg-blue-100 text-blue-700 font-mono">
                                      {typeof afternoonTime === 'string' && afternoonTime.includes(':') ? afternoonTime : formatTime(afternoonTime)}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-gray-400">
                                      Bildirim Yok
                                    </Badge>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* AYLIK MESAİ Tab */}
              <TabsContent value="monthly">
                {/* Month Navigation */}
                <Card className="mb-4">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="h-5 w-5 text-amber-600" />
                        <span className="font-medium text-lg">
                          {monthlyDate.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
                        </span>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {monthlyLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
                  </div>
                ) : monthlyData.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <CalendarIcon className="h-16 w-16 mx-auto mb-4 text-gray-200" />
                      <h3 className="text-xl font-bold mb-2">Veri Bulunmuyor</h3>
                      <p className="text-gray-500">Bu ay için mesai kaydı yok</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CalendarIcon className="h-5 w-5 text-amber-600" />
                        Aylık Mesai Tablosu - {selectedCompany === 'fortis' ? 'FORTİS' : subcontractors.find(s => s.id === selectedCompany)?.company_name || subcontractors.find(s => s.id === selectedCompany)?.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <ScrollArea className="w-full">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50">
                              <TableHead className="sticky left-0 bg-gray-50 z-10 min-w-[150px] font-bold">AD SOYAD</TableHead>
                              {[...Array(getDaysInMonth(monthlyDate))].map((_, i) => (
                                <TableHead key={i} className="text-center min-w-[32px] font-bold text-xs">
                                  {i + 1}
                                </TableHead>
                              ))}
                              <TableHead className="text-center min-w-[40px] font-bold bg-green-50">G</TableHead>
                              <TableHead className="text-center min-w-[40px] font-bold bg-yellow-50">YG</TableHead>
                              <TableHead className="text-center min-w-[40px] font-bold bg-red-50">X</TableHead>
                              <TableHead className="text-center min-w-[40px] font-bold bg-blue-50">Üİ</TableHead>
                              <TableHead className="text-center min-w-[40px] font-bold bg-orange-50">Üsİ</TableHead>
                              <TableHead className="text-center min-w-[40px] font-bold bg-purple-50">R</TableHead>
                              <TableHead className="text-center min-w-[40px] font-bold bg-cyan-50">Yİ</TableHead>
                              <TableHead className="text-center min-w-[40px] font-bold bg-gray-100">RT</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {monthlyData.map(emp => {
                              const att = emp.attendance || {};
                              const counts = {
                                tam_gun: 0, yarim_gun: 0, yok: 0, izinli: 0, 
                                ucretsiz_izinli: 0, raporlu: 0, yillik_izin: 0, tatil: 0
                              };
                              Object.values(att).forEach(status => {
                                if (counts[status] !== undefined) counts[status]++;
                              });
                              
                              return (
                                <TableRow key={emp.id}>
                                  <TableCell className="sticky left-0 bg-white z-10 font-medium">{emp.name}</TableCell>
                                  {[...Array(getDaysInMonth(monthlyDate))].map((_, i) => {
                                    const dayKey = String(i + 1);
                                    const status = att[dayKey];
                                    return (
                                      <TableCell key={i} className="text-center p-1">
                                        {getStatusBadge(status)}
                                      </TableCell>
                                    );
                                  })}
                                  <TableCell className="text-center font-bold bg-green-50">{counts.tam_gun}</TableCell>
                                  <TableCell className="text-center font-bold bg-yellow-50">{counts.yarim_gun}</TableCell>
                                  <TableCell className="text-center font-bold bg-red-50">{counts.yok}</TableCell>
                                  <TableCell className="text-center font-bold bg-blue-50">{counts.izinli}</TableCell>
                                  <TableCell className="text-center font-bold bg-orange-50">{counts.ucretsiz_izinli}</TableCell>
                                  <TableCell className="text-center font-bold bg-purple-50">{counts.raporlu}</TableCell>
                                  <TableCell className="text-center font-bold bg-cyan-50">{counts.yillik_izin}</TableCell>
                                  <TableCell className="text-center font-bold bg-gray-100">{counts.tatil}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                        <ScrollBar orientation="horizontal" />
                      </ScrollArea>
                    </CardContent>
                  </Card>
                )}

                {/* Legend */}
                <Card className="mt-4">
                  <CardContent className="p-4">
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="flex items-center gap-1">{getStatusBadge('tam_gun')} Tam Gün (G)</span>
                      <span className="flex items-center gap-1">{getStatusBadge('yarim_gun')} Yarım Gün (YG)</span>
                      <span className="flex items-center gap-1">{getStatusBadge('yok')} Gelmedi (X)</span>
                      <span className="flex items-center gap-1">{getStatusBadge('izinli')} Ücretli İzinli (Üİ)</span>
                      <span className="flex items-center gap-1">{getStatusBadge('ucretsiz_izinli')} Ücretsiz İzinli (Üsİ)</span>
                      <span className="flex items-center gap-1">{getStatusBadge('raporlu')} Raporlu (R)</span>
                      <span className="flex items-center gap-1">{getStatusBadge('yillik_izin')} Yıllık İzin (Yİ)</span>
                      <span className="flex items-center gap-1">{getStatusBadge('tatil')} Resmi Tatil (RT)</span>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* FAZLA MESAİLER Tab */}
              <TabsContent value="overtime">
                {/* Year/Month Selection */}
                <Card className="mb-4">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-center gap-4">
                      <Select value={String(overtimeYear)} onValueChange={(v) => setOvertimeYear(parseInt(v))}>
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[2024, 2025, 2026].map(y => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={String(overtimeMonth)} onValueChange={(v) => setOvertimeMonth(parseInt(v))}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'].map((m, i) => (
                            <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {overtimeLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
                  </div>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Timer className="h-5 w-5 text-amber-600" />
                        Fazla Mesai Tablosu - {selectedCompany === 'fortis' ? 'FORTİS' : subcontractors.find(s => s.id === selectedCompany)?.company_name || subcontractors.find(s => s.id === selectedCompany)?.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="font-bold">AD SOYAD</TableHead>
                            <TableHead className="font-bold">GÖREV</TableHead>
                            <TableHead className="font-bold">MESLEK</TableHead>
                            <TableHead className="font-bold text-center">HAFTA İÇİ (Saat)</TableHead>
                            <TableHead className="font-bold text-center">HAFTA SONU (Saat)</TableHead>
                            <TableHead className="font-bold text-center">TATİL (Saat)</TableHead>
                            <TableHead className="font-bold text-center">TOPLAM (Saat)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {employees.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                                Bu şirket için çalışan bulunmuyor
                              </TableCell>
                            </TableRow>
                          ) : (
                            employees.map(emp => {
                              let monthRecords = [];
                              const overtimeData = emp.overtime_records;
                              if (overtimeData && typeof overtimeData === 'object') {
                                const yearData = overtimeData[String(overtimeYear)];
                                if (yearData && typeof yearData === 'object') {
                                  const monthData = yearData[String(overtimeMonth)];
                                  if (Array.isArray(monthData)) {
                                    monthRecords = monthData;
                                  }
                                }
                              } else if (Array.isArray(overtimeData)) {
                                monthRecords = overtimeData.filter(r => {
                                  if (!r || !r.date) return false;
                                  const d = new Date(r.date);
                                  return d.getFullYear() === overtimeYear && d.getMonth() + 1 === overtimeMonth;
                                });
                              }
                              
                              const weekday = monthRecords.filter(r => r.type === 'weekday').reduce((sum, r) => sum + (r.hours || 0), 0);
                              const weekend = monthRecords.filter(r => r.type === 'weekend').reduce((sum, r) => sum + (r.hours || 0), 0);
                              const holiday = monthRecords.filter(r => r.type === 'holiday').reduce((sum, r) => sum + (r.hours || 0), 0);
                              const total = weekday + weekend + holiday;
                              
                              return (
                                <TableRow key={emp.id}>
                                  <TableCell className="font-medium">{emp.name}</TableCell>
                                  <TableCell>{emp.position_name || emp.gorev || '-'}</TableCell>
                                  <TableCell>{emp.profession || emp.meslek || '-'}</TableCell>
                                  <TableCell className="text-center">{weekday || '-'}</TableCell>
                                  <TableCell className="text-center">{weekend || '-'}</TableCell>
                                  <TableCell className="text-center">{holiday || '-'}</TableCell>
                                  <TableCell className="text-center font-bold">{total || '-'}</TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default BoardMesaiDurumu;
